
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // 1. นำเข้า useNavigate
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Polyline } from "react-leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { 
  IoRadioOutline, IoWarning, IoClose, IoAddCircle, 
  IoTrashOutline, IoCarOutline, IoArrowForwardCircleOutline 
} from "react-icons/io5";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./ExpressWayMap.css";

// ตรวจสอบว่าไฟล์ Location.js มีอยู่จริง ถ้าไม่มีให้คอมเมนต์บรรทัดนี้แล้วใช้ Mock Data แทน
import { CHALONG_RAT_MAIN_PATH } from "./Location"; 

// --- ฟังก์ชันช่วยเหลือ (Utility Functions) ---

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371e3; // รัศมีโลก (เมตร)
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const snapToRoute = (clickedLat, clickedLng, path) => {
  if (!path) return { lat: clickedLat, lng: clickedLng }; // ป้องกัน Error ถ้า path ไม่มี
  let minDistance = Infinity;
  let snapped = { lat: clickedLat, lng: clickedLng };
  for (let i = 0; i < path.length - 1; i++) {
    const start = { lat: path[i][0], lng: path[i][1] };
    const end = { lat: path[i + 1][0], lng: path[i + 1][1] };
    const dy = end.lat - start.lat; const dx = end.lng - start.lng;
    const mag2 = dy * dy + dx * dx;
    if (mag2 === 0) continue;
    let t = Math.max(0, Math.min(1, ((clickedLat - start.lat) * dy + (clickedLng - start.lng) * dx) / mag2));
    const current = { lat: start.lat + t * dy, lng: start.lng + t * dx };
    const d = Math.pow(clickedLat - current.lat, 2) + Math.pow(clickedLng - current.lng, 2);
    if (d < minDistance) { minDistance = d; snapped = current; }
  }
  return snapped;
};

// --- Icons Configuration ---
const createIcon = (comp, color, className) => L.divIcon({
  html: renderToStaticMarkup(<div style={{ color, fontSize: "30px", display: "flex", filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.4))" }}>{comp}</div>),
  className: className, iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -15]
});

const rsuIcon = createIcon(<IoRadioOutline />, "#007bff", "rsu-marker-class");
const eventIcon = createIcon(<IoWarning />, "#dc3545", "event-marker-class");

// Component ย่อยสำหรับดักจับการคลิกบนแผนที่
function MapClickHandler({ onMapClick }) {
  useMapEvents({ click(e) { 
    // ถ้ามี CHALONG_RAT_MAIN_PATH ให้ Snap ถ้าไม่มีให้ใช้พิกัดที่คลิกเลย
    const s = snapToRoute(e.latlng.lat, e.latlng.lng, CHALONG_RAT_MAIN_PATH || []); 
    onMapClick(s.lat, s.lng); 
  }});
  return null;
}

const ExpressWayMap = () => {
  // 2. เรียกใช้ Hook useNavigate
  const navigate = useNavigate();

  // --- States ---
  const [rsuData, setRsuData] = useState(() => {
    const saved = localStorage.getItem('RSU_POINTS');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [events, setEvents] = useState(() => {
    const saved = localStorage.getItem('MAP_EVENTS');
    return saved ? JSON.parse(saved) : [];
  });

  const [showForm, setShowForm] = useState(false);
  const [inputLat, setInputLat] = useState(null);
  const [inputLon, setInputLon] = useState(null);
  const [nearestRSU, setNearestRSU] = useState(null);
  const [distToRSU, setDistToRSU] = useState(0); 
  const [inputDetail, setInputDetail] = useState("");

  // Sync Data ทั้ง RSU และ Events จาก LocalStorage
  useEffect(() => {
    const syncAll = () => {
      const savedRSU = localStorage.getItem('RSU_POINTS');
      if (savedRSU) setRsuData(JSON.parse(savedRSU));
      const savedEvents = localStorage.getItem('MAP_EVENTS');
      if (savedEvents) setEvents(JSON.parse(savedEvents));
    };
    window.addEventListener('storage', syncAll);
    return () => window.removeEventListener('storage', syncAll);
  }, []);

  const openForm = (lat, lng, fixedRSU = null) => {
    let finalLat = parseFloat(lat);
    let finalLon = parseFloat(lng);

    if (fixedRSU) {
      // ขยับพิกัดเล็กน้อยไม่ให้ทับ Marker เดิม
      finalLat += 0.00008;
      finalLon += 0.00008;
      setNearestRSU(fixedRSU);
      setDistToRSU(0);
    } else {
      // หา RSU ที่ใกล้ที่สุด
      let min = Infinity; let near = null;
      rsuData.forEach(r => {
        const d = calculateDistance(lat, lng, r.lat, r.lon);
        if (d < min) { min = d; near = r; }
      });
      setNearestRSU(near);
      setDistToRSU(min);
    }

    setInputLat(finalLat.toFixed(6));
    setInputLon(finalLon.toFixed(6));
    setInputDetail(""); // เคลียร์ข้อความเก่า
    setShowForm(true);
  };

  // ฟังก์ชันเปลี่ยนหน้า (สำคัญ: แก้ไขแล้ว)
  const goToPage = (page, e, distLabel) => {
    const params = new URLSearchParams({
      id: e.id, 
      lat: e.lat, 
      lon: e.lon, 
      detail: e.detail,
      rsu: e.rsuName, 
      dist: distLabel, 
      time: e.time
    });
    
    // ใช้ navigate แทน window.location.href
    navigate(`/${page}?${params.toString()}`);
  };

  const saveEvent = () => {
    if (!inputDetail) return alert("กรุณาระบุรายละเอียด");
    
    // คำนวณระยะทางเพื่อส่งไปพร้อม URL
    let near = null; let minD = Infinity;
    rsuData.forEach(r => {
      const d = calculateDistance(parseFloat(inputLat), parseFloat(inputLon), r.lat, r.lon);
      if (d < minD) { minD = d; near = r; }
    });
    const distLabel = near ? (minD > 1000 ? (minD / 1000).toFixed(2) + " กม." : minD.toFixed(0) + " ม.") : "0 ม.";

    const newEvent = { 
      id: `EVT-${Date.now()}`, 
      lat: parseFloat(inputLat), 
      lon: parseFloat(inputLon), 
      rsuId: nearestRSU?.id || "N/A",
      rsuName: nearestRSU?.name || "Unknown",
      detail: inputDetail, 
      time: new Date().toLocaleTimeString(),
      distLabel: distLabel
    };

    // 1. บันทึกลง State และ LocalStorage
    const updatedEvents = [...events, newEvent];
    setEvents(updatedEvents);
    localStorage.setItem('MAP_EVENTS', JSON.stringify(updatedEvents));

    // 2. ปิดฟอร์ม
    setShowForm(false);

    // 3. Auto Send to OBU Page
    goToPage("obu", newEvent, distLabel);
  };

  const deleteEvent = (id) => {
    if (window.confirm("ลบเหตุการณ์นี้?")) {
      const updated = events.filter(e => e.id !== id);
      setEvents(updated);
      localStorage.setItem('MAP_EVENTS', JSON.stringify(updated));
    }
  };

  return (
    <div className="map-wrapper" style={{ width: "100%", height: "100vh", position: "relative" }}>
      {showForm && (
        <div className="map-controls-overlay">
          <div className="form-header">
            <strong>📝 แจ้งเหตุการณ์ (Auto-Send to OBU)</strong> 
            <button onClick={() => setShowForm(false)} className="close-btn"><IoClose /></button>
          </div>
          <div className="group-input">
            <div className="boundary-info-box">📍 ใกล้: {nearestRSU?.name || "ไม่ระบุ"}</div>
            <textarea 
                value={inputDetail} 
                onChange={(e) => setInputDetail(e.target.value)} 
                placeholder="รายละเอียดเหตุการณ์..." 
                autoFocus 
            />
          </div>
          <button onClick={saveEvent} className="btn-confirm">บันทึกและส่งข้อมูลรถยนต์</button>
        </div>
      )}

      <MapContainer center={[13.775, 100.605]} zoom={13} style={{ height: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapClickHandler onMapClick={(lat, lng) => openForm(lat, lng)} />
        {/* แสดงเส้นทาง (ถ้ามี) */}
        {CHALONG_RAT_MAIN_PATH && (
            <Polyline positions={CHALONG_RAT_MAIN_PATH} color="#28a745" weight={6} opacity={0.7} />
        )}

        {rsuData.map((rsu) => (
          <Marker key={rsu.id} position={[rsu.lat, rsu.lon]} icon={rsuIcon}>
            <Popup>
              <div style={{ minWidth: "160px" }}>
                <strong>📡 {rsu.name}</strong><hr/>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button onClick={() => openForm(rsu.lat, rsu.lon, rsu)} className="rsu-add-btn" style={{ flex: 1 }}><IoAddCircle /> Event</button>
                  {/* ใช้ navigate ในการจัดการแทน window.location ถ้าต้องการเปลี่ยนหน้านี้ด้วย */}
                  <button onClick={() => navigate(`/rsu-page?id=${rsu.id}`)} className="rsu-add-btn" style={{ background: '#6c757d', flex: 1 }}><IoArrowForwardCircleOutline /> จัดการ</button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {events.map(e => {
          let near = null; let minD = Infinity;
          rsuData.forEach(r => {
            const d = calculateDistance(e.lat, e.lon, r.lat, r.lon);
            if (d < minD) { minD = d; near = { ...r, distance: d }; }
          });
          const distLabel = (near && typeof near.distance === 'number') 
                ? (near.distance > 1000 ? (near.distance / 1000).toFixed(2) + " กม." : near.distance.toFixed(0) + " ม.")
                : "0 ม.";

          return (
            <Marker key={e.id} position={[e.lat, e.lon]} icon={eventIcon}>
              <Popup>
                <div style={{ textAlign: "center", minWidth: "200px" }}>
                  <b style={{ color: "#dc3545" }}>⚠️ {e.detail}</b>
                  <div style={{ margin: "8px 0", padding: "8px", background: "#f8f9fa", borderRadius: "8px", fontSize: "11px", textAlign: 'left' }}>
                    <strong>📡 สถานี:</strong> {near ? near.name : "N/A"}<br/>
                    <strong>📏 ระยะห่าง:</strong> {distLabel}
                  </div>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button onClick={() => goToPage("obu", e, distLabel)} className="btn-obu" style={{ background: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '8px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                      <IoCarOutline /> OBU
                    </button>
                    <button onClick={() => deleteEvent(e.id)} className="btn-del" style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '8px', flex: 1 }}>
                      <IoTrashOutline /> ลบ
                    </button>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default ExpressWayMap;